

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token   = localStorage.getItem('accessToken');

export default function TrustSection({ caseId, sofaResponse }) {
  const [entries, setEntries] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({
    trust_name: '',
    description: '',
    transfer_date: ''
  });
  const [editEntry, setEditEntry] = useState(null);

  // Fetch existing trust transfers
  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-property-transferred-to-trust/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch trusts');
        setEntries(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  // Open Add modal
  function openAddModal() {
    setNewEntry({ trust_name: '', description: '', transfer_date: '' });
    setModalOpen(true);
  }

  // Open Edit modal
  function openEditModal(item) {
    setEditEntry({ ...item });
    setEditModalOpen(true);
  }

  // Save new
  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-property-transferred-to-trust/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newEntry)
        }
      );
      if (!res.ok) throw new Error('Failed to add entry');
      const created = await res.json();
      setEntries(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Save edit
  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-property-transferred-to-trust/${editEntry.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editEntry)
        }
      );
      if (!res.ok) throw new Error('Failed to update entry');
      const updated = await res.json();
      setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Delete
  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-property-transferred-to-trust/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to delete entry');
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 19: Property Transferred to Trust
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List asset-protection or trust that you have transfered property and you are a beneficiary within the last 10 years.
        </p>
      {entries.length === 0 ? (
        <p>No trust transfers recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {entries.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => { e.stopPropagation(); handleDelete(item.id); }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000 
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4 
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3 
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2 
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 
                    00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.trust_name}</h3>
              <p className="mt-2 text-sm"><strong>Description:</strong> {item.description}</p>
              <p className="mt-2 text-sm"><strong>Date:</strong> {item.transfer_date || '—'}</p>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Trust Transfer
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Trust Transfer</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Trust Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newEntry.trust_name}
                onChange={e => setNewEntry(prev => ({ ...prev, trust_name: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={newEntry.description}
                onChange={e => setNewEntry(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Transfer Date</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={newEntry.transfer_date}
                onChange={e => setNewEntry(prev => ({ ...prev, transfer_date: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Trust Transfer</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Trust Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editEntry.trust_name || ''}
                onChange={e => setEditEntry(prev => ({ ...prev, trust_name: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={editEntry.description || ''}
                onChange={e => setEditEntry(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Transfer Date</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={editEntry.transfer_date || ''}
                onChange={e => setEditEntry(prev => ({ ...prev, transfer_date: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}